import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import type { UserDetailsDto, UserListItemDto, UserProjectDto } from "@/api/types";
import { FormRadioCardGroup } from "@/components/Form/FormRadioCard";
import { AlertTriangleIcon } from "@/components/Icons/AlertTriangleIcon";
import { ClockPlusIcon } from "@/components/Icons/ClockPlusIcon";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { RadioCard } from "@/components/Radio/RadioCard";
import { Box } from "@/design-system/Box";
import { Button } from "@/design-system/Button";
import { Checkbox } from "@/design-system/Checkbox";
import { Inline } from "@/design-system/Inline";
import { MultiSelect } from "@/design-system/MultiSelect";
import { Notice } from "@/design-system/Notice";
import { Stack } from "@/design-system/Stack";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useSlug } from "@/hooks/useSlug";
import { userMutations } from "@/queries/users/mutations";
import { useUserQueries } from "@/queries/users/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { ScheduleUserDeletionModal } from "./ScheduleUserDeletionModal";

enum DeleteAdminModalStep {
  SELECT_PROJECTS,
  CONFIRM_DELETION,
}

type DeleteAdminScope = "currentProject" | "multipleProjects";

type DeleteAdminModalProps = ModalBaseProps & {
  user: UserDetailsDto | UserListItemDto;
};

export function DeleteAdminModal({
  user,
  isOpened,
  onOpenChange,
  onExitComplete,
}: DeleteAdminModalProps): React.ReactNode {
  const [currStep, setCurrStep] = useState<DeleteAdminModalStep | undefined>(undefined);
  const defaultScope: DeleteAdminScope = "multipleProjects";
  const [selectedScope, setSelectedScope] = useState<DeleteAdminScope>(defaultScope);
  const [selectedProjects, setSelectedProjects] = useState<UserProjectDto[]>([]);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isSetDeletionDateModalOpened, setDeletionModalOpenHandlers] = useBool(false);

  const sessionUser = useSessionUser();
  const slug = useSlug();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const userQueries = useUserQueries();
  const { data: userLinkedProjects = [] } = useQuery({
    ...userQueries.listLinkedProjects({ userId: user.id ?? "" }),
    enabled: isOpened,
  });
  const deleteUserMutation = userMutations.useDeleteUser();

  const isDeletionScheduled = Boolean(user.deleteAfter);
  const currProject = userLinkedProjects.find((p) => p.id === sessionUser.projectId);
  const otherProjects = userLinkedProjects.filter((p) => p.id !== sessionUser.projectId);
  const isSelectingProjectsAllowed = otherProjects.length > 0 && !isDeletionScheduled;
  const defaultStep: DeleteAdminModalStep | undefined = useMemo(() => {
    if (userLinkedProjects.length === 0) return undefined;

    return isSelectingProjectsAllowed ? DeleteAdminModalStep.SELECT_PROJECTS : DeleteAdminModalStep.CONFIRM_DELETION;
  }, [userLinkedProjects, isSelectingProjectsAllowed]);

  useEffect(() => {
    if (isOpened) {
      // oxlint-disable-next-line react-hooks/set-state-in-effect
      setCurrStep(defaultStep);

      if (defaultStep === DeleteAdminModalStep.SELECT_PROJECTS && userLinkedProjects.length > 0) {
        setSelectedProjects(userLinkedProjects);
      } else if (defaultStep === DeleteAdminModalStep.CONFIRM_DELETION && currProject) {
        setSelectedProjects([currProject]);
      }
    }
  }, [isOpened, defaultStep, userLinkedProjects, sessionUser.projectId, currProject]);

  const handleSelectProjects = (projects: UserProjectDto[]) => {
    if (currProject && !projects.find((p) => p.id === currProject.id)) {
      setSelectedProjects([currProject, ...projects]);
    } else {
      setSelectedProjects(projects);
    }
  };

  const handleConfirmProjects = () => {
    setCurrStep(DeleteAdminModalStep.CONFIRM_DELETION);
  };

  const handleConfirmDeletion = () => {
    if (!user) return;

    deleteUserMutation.mutate(
      { userId: user.id, projectIds: selectedProjects.map((p) => p.id) },
      {
        onSuccess: () => {
          if (!sessionUser.isSuperAdmin) {
            void navigate(routes.users.list({ slug }));
          }

          onOpenChange(false);
        },
      },
    );
  };

  let description = "";
  if (currStep === DeleteAdminModalStep.CONFIRM_DELETION && isDeletionScheduled) {
    description = t("page.user-detail.delete-or-date-modal.description-already-set");
  }

  return (
    <>
      <Modal.Root
        title={t("component.admin-deletion-modal.title", {
          fullName: `${user.firstName} ${user.lastName}`,
        })}
        description={description}
        data-testid="delete-admin-modal"
        isPending={currStep === undefined || !currProject}
        shouldCloseOnClickOutside={false}
        shouldCloseOnEsc={false}
        onExitComplete={() => {
          setIsConfirmed(false);
          setCurrStep(undefined);
          setSelectedScope(defaultScope);
          onExitComplete?.();
        }}
        {...{ isOpened, onOpenChange }}
      >
        <Modal.Body>
          {currStep === DeleteAdminModalStep.SELECT_PROJECTS && (
            <section className="flex flex-1 flex-col gap-space-md">
              <p>{t("component.admin-deletion-modal.user-exists-in-other-projects.body")}</p>
              <FormRadioCardGroup>
                <RadioCard
                  label={t("component.admin-deletion-modal.options.multiple-projects")}
                  value="multipleProjects"
                  name="scope"
                  checked={selectedScope === "multipleProjects"}
                  onChange={() => {
                    setSelectedScope("multipleProjects");
                    setSelectedProjects(userLinkedProjects);
                  }}
                />
                <RadioCard
                  label={t("component.admin-deletion-modal.options.current-project", {
                    projectName: sessionUser.project.name,
                  })}
                  value="currentProject"
                  name="scope"
                  checked={selectedScope === "currentProject"}
                  onChange={() => {
                    setSelectedScope("currentProject");
                    setSelectedProjects(currProject ? [currProject] : []);
                  }}
                />
              </FormRadioCardGroup>
              {selectedScope === "multipleProjects" && (
                <section className="flex w-full flex-col gap-space-sm rounded-sm bg-background-secondary p-card-compact">
                  <MultiSelect<UserProjectDto>
                    placeholder={t("component.admin-deletion-modal.other-projects.placeholder")}
                    items={userLinkedProjects}
                    unclearable={currProject ? [currProject] : []}
                    renderOption={(x) => x.name}
                    keySelector={(x) => x.id}
                    selected={selectedProjects}
                    onChange={handleSelectProjects}
                    usePortal
                  />
                  <Inline gap="md" wrap={false}>
                    <Button
                      size="sm"
                      variant="ghostPrimary"
                      onClick={() =>
                        setSelectedProjects([currProject, ...otherProjects].filter(Boolean) as UserProjectDto[])
                      }
                      data-testid="add-all-projects-btn"
                      disabled={
                        userLinkedProjects.length === 0 || selectedProjects.length === userLinkedProjects.length
                      }
                    >
                      {t("component.admin-deletion-modal.btn.add-all-projects", {
                        count: userLinkedProjects.length,
                      })}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghostPrimary"
                      onClick={() => setSelectedProjects([currProject].filter(Boolean) as UserProjectDto[])}
                      disabled={
                        userLinkedProjects.length === 0 ||
                        (selectedProjects.length === 1 && selectedProjects[0].id === sessionUser.projectId)
                      }
                    >
                      {t("common.action.clear")}
                    </Button>
                  </Inline>
                </section>
              )}
            </section>
          )}
          {currStep === DeleteAdminModalStep.CONFIRM_DELETION && (
            <Box padding={{ bottom: "md" }}>
              <Stack gap="md">
                {selectedProjects.length > 1 && (
                  <Notice
                    icon={<AlertTriangleIcon />}
                    variant="warning"
                    message={t("component.admin-deletion-modal.notice.multiple-projects", {
                      count: selectedProjects.length,
                    })}
                  />
                )}
                <Checkbox
                  data-testid="confirm-delete-checkbox"
                  id="checkbox-confirm"
                  name="isConfirmed"
                  checked={isConfirmed}
                  onChange={(e) => setIsConfirmed(e.target.checked)}
                  label={t("page.user-detail.delete-or-date-modal.confirm-checkbox")}
                />
              </Stack>
            </Box>
          )}
        </Modal.Body>
        <Modal.Actions>
          {currStep === DeleteAdminModalStep.SELECT_PROJECTS && (
            <Button onClick={handleConfirmProjects} data-testid="next-btn">
              {t("page.user-detail.delete-or-date-modal.btn.continue", {
                count: selectedProjects.length,
              })}
            </Button>
          )}
          {currStep === DeleteAdminModalStep.CONFIRM_DELETION && (
            <>
              {isSelectingProjectsAllowed && (
                <span className="w-fit md:mr-auto">
                  <Button onClick={() => setCurrStep(DeleteAdminModalStep.SELECT_PROJECTS)} variant="outline">
                    {t("common.action.back")}
                  </Button>
                </span>
              )}
              <Button
                onClick={setDeletionModalOpenHandlers.setTrue}
                data-testid="delete-user-modal-set-deletion-btn"
                variant="outline"
                icon={<ClockPlusIcon />}
                disabled={!isConfirmed && !isDeletionScheduled}
              >
                {isDeletionScheduled
                  ? t("page.user-detail.delete-or-date-modal.action.update-deletion-date")
                  : t("page.user-detail.delete-or-date-modal.action.set-deletion-date")}
              </Button>
              <Button
                onClick={handleConfirmDeletion}
                variant="filled"
                tone="error"
                data-testid="confirm-delete-btn"
                isLoading={deleteUserMutation.isPending}
                disabled={!isConfirmed}
              >
                {t("page.user-detail.delete-or-date-modal.action.delete")}
              </Button>
            </>
          )}
        </Modal.Actions>
      </Modal.Root>

      <ScheduleUserDeletionModal
        user={user}
        // If the deletion date is already set, only allow updating for the current project
        projectIds={isDeletionScheduled ? [sessionUser.project.id] : selectedProjects.map((p) => p.id)}
        isOpened={isSetDeletionDateModalOpened}
        onOpenChange={setDeletionModalOpenHandlers.set}
        onSetDeletionDate={() => onOpenChange(false)}
      />
    </>
  );
}
